import { Injectable, NgZone } from '@angular/core';
import { DatePipe } from '@angular/common';
import { IspmHttpClient } from "@ispm/core/services/ispm-http-client";
import { AuthenticationService } from "@ispm/user/services/authentication.service";
import { Observable, Subject } from "rxjs";
import { repeatWhen, take, takeUntil } from "rxjs/operators";
import { ExportWorklistDataService } from "./export-worklist-data.service";
import { ActivityService } from "@ispm/user/services/activity.service";
import { ActivityActions } from "@ispm/user/constants/activityActions";
import { NotificationMessageTypes } from "@ispm/core/constants/notification-message-types.constant";
import { NOTIFICATION_MESSAGE } from "@ispm-client/modules/aggregator/services/consts";
import { ExportNotificationService } from "./export-notification-service";
import {ExportType} from "../constants/export-types.constant";
import { DynamicApiService } from "./dynamic-api.service";
import {FilterService} from "../../filter/services/filter.service";
import {findIndex, get,isEmpty} from "lodash";

@Injectable()
export class ExportWorklistDataPollingService {
  private readonly _runPolling$: Subject<void> = new Subject<void>();
  private readonly _stopPolling$: Subject<void> = new Subject<void>();
  private _destroyed$: Subject<any> = new Subject();

  private pollingCreated: boolean;
  private isSameSession: boolean;

  constructor(private _ispmHttpClient: IspmHttpClient,
              private _authService: AuthenticationService,
              private _exportWorklistDataService: ExportWorklistDataService,
              private _exportNotificationService:ExportNotificationService,
              private _activityService: ActivityService,
              private _notificationMessage: NOTIFICATION_MESSAGE,
              private _ngZone: NgZone,
              private _datePipe: DatePipe,
              private dynamicApiService: DynamicApiService,
              private filterService: FilterService) {
  }

  public register = () => {
    // Called when worklist is loaded, polling should be initialized only once
    if (!this.pollingCreated) {
      this.pollingCreated = true;
      this._exportWorklistDataService.startPollingSubject
        .pipe(takeUntil(this._destroyed$.asObservable()))
        .subscribe(() => {
          this.startPolling(true);
        });

      this._ngZone.runOutsideAngular(() => {
        this.initPolling();
        this.startPolling(false);
      });
    }
  }

  public unregister = () => {
    this._stopPolling$.next();
    this._stopPolling$.complete();
    this._runPolling$.next();
    this._runPolling$.complete();
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  public initPolling = () => {
    //Polling for worklist data export tasks the current user and the worklist
    //The first iteration is immediate, yet the following is by delay of the 'pollingInterval' value
    this._exportWorklistDataService.loadExportDataConfiguration()
      .pipe(take(1))
      .subscribe((config) => {
        let pollingInterval = config ? (config.pollingIntervalSec * 1000) : 15000;
        return Observable.interval(0)
          .pipe(
            takeUntil(this._stopPolling$.asObservable()),
            repeatWhen(() => this._runPolling$.delay(pollingInterval))
          )
          .subscribe(() => {
            this.pollActiveTasks(this._authService.user.id);
          })
      });
  }

  /**
  This provides an requestor to trigger
   */
  public requestPoll(isSameSession:boolean = false){
    let success = false;
    if(this._runPolling$){
      success = true;
      this.startPolling(isSameSession);
      return success;
    }
    return success;
  }

  private pollActiveTasks = (actorId: string) => {
    this.stopPolling();
    let exportBasedOnActor = true;
    this._exportWorklistDataService.getExportDataActiveTasks(exportBasedOnActor ? actorId : null,null,null)
      .pipe(take(1))
      .subscribe((activities: any[]) => {
        if (activities && activities.length > 0) {
          activities.forEach(activity => {
            if (activity.context.some(resource => resource.reference === ExportType.ACR)) {
              this.exportDataACRTaskProcess(activities, actorId, activity.context[activity.context.findIndex(resource => resource.resourceType === 'exportTitle')].reference);
            } else {
              this.exportDataExcelProcess(activities, actorId);
            }
          })
        } else {
          this._exportWorklistDataService.setExportWorklistDataOptionDisabledStatus({
            disabled: false,
            exportRunning: false
          }, null, true);
        }
      });
  }


  private exportDataExcelProcess(activities:any[],actorId:any){
      let completedActivities = 0;
      activities.forEach((activity, idx) => {
        let taskId = activity.context.length > 0 && activity.context[0].reference ? activity.context[0].reference : undefined;
        let worklistTemplateId = activity.context.length > 1 && activity.context[1].reference ? activity.context[1].reference : undefined;
        if (taskId) {
          this._exportWorklistDataService.getWorklistExportToExcelTaskData(taskId)
            .then((result) => {
              switch (result.status) {
                case 'COMPLETED': {
                  this._exportWorklistDataService.setExportWorklistDataOptionDisabledStatus({disabled:false,exportRunning:false}, worklistTemplateId);
                  this._activityService.updateActivityAction(activity, actorId, ActivityActions.READ);
                  completedActivities++;
                  let fileName = result.globalParameters?.worklistAlias ?
                    result.globalParameters?.worklistAlias.replace(' ', '') + '_WorklistExport-' + this._datePipe.transform(new Date(), 'YYYY-MM-ddTHH-mm') + '.xlsx' : null;
                  if (this.isSameSession) {
                    this._exportWorklistDataService.displayExcel(JSON.parse(result.lastActionResult.responseBody), fileName);
                  } else {
                    //Send a notification message with a link to the file downloading
                    this.sendExportDataCompletedNotification(result, fileName);
                  }
                  break;
                }
                case 'FAILED': {
                  console.error(`getWorklistExportToExcelTaskData :: export data task failed => task ID: ${JSON.stringify(taskId)} - error: ${result.errMessage}"`);
                  this._exportWorklistDataService.setExportWorklistDataOptionDisabledStatus({disabled:false,exportRunning:false}, worklistTemplateId);
                  this._activityService.updateActivityAction(activity, actorId, ActivityActions.FAILED);
                  this.sendExportDataFailedNotification(result.globalParameters?.worklistAlias,this._notificationMessage.ASYNC_FAILED_TO_EXPORT_DATA_TO_EXCEL);
                  completedActivities++;
                  break;
                }
                default: {
                  if (activity.action === ActivityActions.EXPIRED) {
                    this._exportWorklistDataService.setExportWorklistDataOptionDisabledStatus({disabled:false,exportRunning:false}, worklistTemplateId);
                    this._activityService.updateActivityAction(activity, actorId, ActivityActions.EXPIRED);
                    this.sendExportDataExpiredNotification(result.globalParameters?.worklistAlias, this._notificationMessage.ASYNC_FAILED_TO_EXPORT_DATA_TO_EXCEL_EXPIRED);
                    completedActivities++;
                  } else {
                    //Update activity sample time
                    this._activityService.updateActivitySampleTime(activity);
                  }
                }
              }
            })
            .catch(err => {
              console.error(`getWorklistExportToExcelTaskData :: export data task failed => task ID: ${JSON.stringify(taskId)} - error: ${err}"`);
              this._exportWorklistDataService.setExportWorklistDataOptionDisabledStatus({disabled:false,exportRunning:false}, worklistTemplateId);
              this._activityService.updateActivityAction(activity, actorId, ActivityActions.FAILED);
              this.sendExportDataFailedNotification('',this._notificationMessage.ASYNC_FAILED_TO_EXPORT_DATA_TO_EXCEL);
              completedActivities++;
              return Observable.of(false);
            })
            .finally(() => {
              if (activities.length === (idx + 1) && activities.length > completedActivities) {
                this.startPolling(this.isSameSession);
              }
            })
        } else {
          if (activity.action === ActivityActions.EXPIRED) {
            this._exportWorklistDataService.setExportWorklistDataOptionDisabledStatus({disabled:false,exportRunning:false}, worklistTemplateId);
            this._activityService.updateActivityAction(activity, actorId, ActivityActions.EXPIRED);
            this.sendExportDataExpiredNotification('',this._notificationMessage.ASYNC_FAILED_TO_EXPORT_DATA_TO_EXCEL_EXPIRED);
            completedActivities++;
          } else {
            //Update activity sample time
            this._activityService.updateActivitySampleTime(activity);
          }
          if (activities.length === (idx + 1) && activities.length > completedActivities) {
            this.startPolling(this.isSameSession);
          }
        }
      });

  }

  private exportDataACRTaskProcess(activities:any[],actorId:any,exportTitle:string){
    let completedActivities = 0;
    activities.forEach((activity, idx) => {
      let taskId = activity.context.length > 0 && activity.context[0].reference ? activity.context[0].reference : undefined;
      let worklistTemplateId = activity.context.length > 1 && activity.context[1].reference ? activity.context[1].reference : undefined;
      let currentPractitionerId = this._authService.user.id !== null && this._authService.user.id !== undefined? this._authService.user.id: null;
      let activityActivityPId = get(activity, 'actor.reference', null);
      if (taskId) {
        this._exportWorklistDataService.getWorklistExportToExcelTaskData(taskId)
          .then((result) => {
            switch (result.status) {
              case 'COMPLETED': {
                if (currentPractitionerId !== null && activityActivityPId !== null && currentPractitionerId === activityActivityPId) {
                  this._activityService.updateActivityAction(activity, actorId, ActivityActions.READ).then( () => {
                    this._exportWorklistDataService.setExportWorklistDataOptionDisabledStatus({disabled:false,exportRunning:false}, worklistTemplateId);
                  });
                  completedActivities++;
                  // Check if confirmedbyregistry do not download file
                  const action0 = JSON.parse(result.globalParameters.action0);
                  const isExport = action0['derivedStatus'] != 'confirmedbyregistry';
                  if (isExport) {
                    if (this.isSameSession) {
                          this._exportWorklistDataService.JSONToTextConvertor(JSON.parse(result.lastActionResult.responseBody), exportTitle);
                        }
                    else {
                      // Send a notification message with a link to the file downloading
                      this.sendExportDataACRCompletedNotification(result, exportTitle);
                    }
                  }
                  let requestedCount: number;
                  if (Array.isArray(JSON.parse(result.lastActionResult.responseBody))) {
                    requestedCount = isNaN(+result.globalParameters.requestedDiagnosticOrderCount) ? null : +result.globalParameters.requestedDiagnosticOrderCount;
                    let maxExportLimit = activity.context.findIndex(item => item.resourceType === 'maxExportLimit');
                    if (maxExportLimit > -1) {
                      maxExportLimit = activity.context[maxExportLimit].reference;
                    }
                    let processedCount = isNaN(+JSON.parse(result.lastActionResult.responseBody).length) ? 0 : +JSON.parse(result.lastActionResult.responseBody).length;
                    let message: string;
                    let typeOfNotification: string;
                    if (!isExport) {
                      message = "All records processed successfully. Total processed records: " + requestedCount;
                      typeOfNotification = NotificationMessageTypes.INFO;
                    } else {
                      message = ((requestedCount - processedCount) === 0) ? "All records processed successfully. Total processed records: " + processedCount :
                        "\nFew records failed. Please retry\n Successfully Processed : " +  processedCount +"\n Records Failed : "+ (requestedCount - processedCount);
                      typeOfNotification = (requestedCount - processedCount) === 0 ? NotificationMessageTypes.INFO : NotificationMessageTypes.WARN;
                    }
                    this._exportNotificationService.setNotificationMessage(typeOfNotification,
                      message, null, null, null, false, false, true);
                  }
                  // refresh worklist filter once downloaded or confirmedbyregistry
                  this.filterService.filterChanged();
                  this.dynamicApiService.setReloadWorklistStatus(true);
                }
                break;
              }
              case 'FAILED': {
                console.error(`getWorklistExportToExcelTaskData :: export data task failed => task ID: ${JSON.stringify(taskId)} - error: ${result.errMessage}"`);
                this._exportWorklistDataService.setExportWorklistDataOptionDisabledStatus({ disabled: false, exportRunning: false}, worklistTemplateId);
                if (currentPractitionerId !== null && activityActivityPId !== null && currentPractitionerId === activityActivityPId) {
                  this._activityService.updateActivityAction(activity, actorId, ActivityActions.FAILED);
                  this.sendExportDataFailedNotification(exportTitle, this._notificationMessage.ASYNC_FAILED_TO_EXPORT_DATA_TO_EXCEL);
                  completedActivities++;
                  this.dynamicApiService.setReloadWorklistStatus(true);
                  this.filterService.filterChanged();
                }
                break;
              }
              default: {
                if (activity.action === ActivityActions.EXPIRED) {
                  this._exportWorklistDataService.setExportWorklistDataOptionDisabledStatus({ disabled: false, exportRunning: false}, worklistTemplateId);
                  if (currentPractitionerId !== null && activityActivityPId !== null && currentPractitionerId === activityActivityPId) {
                    this._activityService.updateActivityAction(activity, actorId, ActivityActions.EXPIRED);
                    this.sendExportDataExpiredNotification(exportTitle, this._notificationMessage.ASYNC_FAILED_TO_EXPORT_DATA_TO_EXCEL_EXPIRED);
                    completedActivities++;
                    this.dynamicApiService.setReloadWorklistStatus(true);
                    this.filterService.filterChanged();
                  }
                } else {
                  // Update activity sample time
                  this._activityService.updateActivitySampleTime(activity);
                }
              }
            }
          })
          .catch(err => {
            console.error(`getWorklistExportToExcelTaskData :: export data task failed => task ID: ${JSON.stringify(taskId)} - error: ${err}"`);
            this._exportWorklistDataService.setExportWorklistDataOptionDisabledStatus({ disabled: false, exportRunning: false}, worklistTemplateId);
            this._activityService.updateActivityAction(activity, actorId, ActivityActions.FAILED);
            this.sendExportDataFailedNotification('', this._notificationMessage.ASYNC_FAILED_TO_EXPORT_DATA_TO_EXCEL);
            completedActivities++;
            return Observable.of(false);
          })
          .finally(() => {
            if (activities.length === (idx + 1) && activities.length > completedActivities) {
              this.startPolling(this.isSameSession);
            }
          });
      } else {
        if (activity.action === ActivityActions.EXPIRED) {
          this._exportWorklistDataService.setExportWorklistDataOptionDisabledStatus({ disabled: false, exportRunning: false}, worklistTemplateId);
          if (currentPractitionerId !== null && activityActivityPId !== null && currentPractitionerId === activityActivityPId){
            this._activityService.updateActivityAction(activity, actorId, ActivityActions.EXPIRED);
            this.sendExportDataExpiredNotification('', this._notificationMessage.ASYNC_FAILED_TO_EXPORT_DATA_TO_EXCEL_EXPIRED);
            completedActivities++;
          }
        } else {
          // Update activity sample time
          this._activityService.updateActivitySampleTime(activity);
        }
        if (activities.length === (idx + 1) && activities.length > completedActivities) {
          this.startPolling(this.isSameSession);
        }
      }
    });

  }

  private sendExportDataCompletedNotification = (data: any, fileName: string) => {
    let buttons = [{
      label: this._notificationMessage.ASYNC_EXPORT_DATA_TO_EXCEL_COMPLETED_BUTTON,
      action: () => this._exportWorklistDataService.displayExcel(JSON.parse(data.lastActionResult.responseBody), fileName)
    }];

    let messageParam = {
      textToReplace: '<WORKLIST_NAME>',
      value: data.globalParameters?.worklistAlias
    }

    this._exportNotificationService.setNotificationMessage(NotificationMessageTypes.SUCCESS,
      this._notificationMessage.ASYNC_EXPORT_DATA_TO_EXCEL_COMPLETED_TITLE, messageParam, null,
      this._notificationMessage.ASYNC_EXPORT_DATA_TO_EXCEL_COMPLETED_BODY, false, false, true, buttons);
  }

  private sendExportDataACRCompletedNotification = (data: any, fileName: string) => {
    let buttons = [{
      label: this._notificationMessage.ASYNC_EXPORT_DATA_TO_EXCEL_COMPLETED_BUTTON,
      action: () => this._exportWorklistDataService.JSONToTextConvertor(JSON.parse(data.lastActionResult.responseBody), fileName)
    }];

    let messageParam = {
      textToReplace: '<WORKLIST_NAME>',
      value: fileName
    }

    this._exportNotificationService.setNotificationMessage(NotificationMessageTypes.SUCCESS,
      this._notificationMessage.ASYNC_EXPORT_DATA_TO_EXCEL_COMPLETED_TITLE, messageParam, null,
      this._notificationMessage.ASYNC_EXPORT_DATA_TO_EXCEL_COMPLETED_BODY, false, false, true, buttons);
  }

  private sendExportDataExpiredNotification = (paramValue: string,notificationMessage: string) => {
    let messageParam = {
      textToReplace: '<WORKLIST_NAME>',
      value: paramValue
    }
    this._exportNotificationService.setNotificationMessage(NotificationMessageTypes.ERROR,notificationMessage, messageParam, null,null, false, false, true);
  }

  private sendExportDataFailedNotification = (paramValue: string,notificationMessage:string) => {
    let messageParam = {
      textToReplace: '<WORKLIST_NAME>',
      value: paramValue
    }
    this._exportNotificationService.setNotificationMessage(NotificationMessageTypes.ERROR,notificationMessage, messageParam, null,null, false, false, true);
  }

  private startPolling = (isSameSession) => {
    this.isSameSession = isSameSession;
    this._runPolling$.next();
  }

  private stopPolling = () => {
    this._stopPolling$.next();
  }

}
